import { ListMagnifyingGlassIcon } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import * as styles from './segments-labels.css'

export const NoAnalysisCircle: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.noAnalysisCircle}
  >
    <circle cx="12" cy="12" r="11" strokeWidth="2" strokeDasharray="6 6" />
  </svg>
)

export type SegmentLabelProps<TName extends string = string> = {
  name: TName
  percentage: number
  subLabel: string
  color: string
  icon?: ReactNode
}

type SegmentsLabelsProps = {
  labels: SegmentLabelProps[]
  /**
   * The glyph left of the whole legend. Omitted entirely with `false`.
   *
   * Defaults to a magnifying glass, which is what every bar on the estate card shows. A legend narrow
   * enough to be read as a key — one line of bands under a table row's bar — has no room to spend on a
   * decorative 48px slot, so it opts out rather than passing something quieter.
   */
  icon?: ReactNode | false
  /**
   * Names the dimension the segments divide, for a legend whose labels do not say it themselves.
   *
   * "Yes / Partial / No / Unknown" means nothing without the question it answers. Bars whose segment
   * names carry their own meaning — "True positive", "Exploitable" — need no heading and render exactly
   * as before when this is omitted.
   */
  heading?: string
}

const SegmentLabel: React.FC<SegmentLabelProps> = label => (
  <div className={styles.label}>
    {label.icon ?? (
      <span
        data-testid={`segment-label-dot-${label.name}`}
        className={styles.dot}
        style={{ backgroundColor: label.color }}
      />
    )}
    <div>
      <div className={styles.labelName}>
        {label.name} ({label.percentage.toFixed(0)}%)
      </div>
      <div className={styles.subLabel}>{label.subLabel}</div>
    </div>
  </div>
)

export const SegmentsLabels: React.FC<SegmentsLabelsProps> = ({ labels, icon, heading }) => {
  const row = (
    // Without a heading the row *is* the card, exactly as before. With one, the card moves outward and the
    // row keeps only its layout — so adding a heading cannot shift the existing bars' legends.
    <div className={heading === undefined ? styles.container : styles.row}>
      {icon !== false && (
        <div className={styles.leadingIcon}>
          {icon ?? <ListMagnifyingGlassIcon className={styles.leadingIconSvg} weight="light" />}
        </div>
      )}
      {labels.map(label => (
        <SegmentLabel key={label.name} {...label} />
      ))}
    </div>
  )

  if (heading === undefined) {
    return row
  }

  return (
    <div className={styles.headedContainer}>
      <div className={styles.heading}>{heading}</div>
      {row}
    </div>
  )
}
